import React, { useState, useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import { Collapse, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { withAnalytic } from '@skooldio/analytics-provider/dist/Analytics/hoc';
import { NetworkStatus } from 'apollo-boost';

import { Flex, Box, Icon, ICON_LIST, Text, Image } from 'Components/Base';
import Breadcrumb from 'Components/Breadcrumb';
import ButtonIcon from 'Components/ButtonIcon';
import Loading from 'Components/Loading';
import AttendanceFilter, { getFilterData, convertToBreadCrumb } from 'Containers/AttendanceFilter';
import AttendanceFilterData from 'Domains/AttendanceFilterData';
import { useAnalytics } from 'Libs/Analytics';
import * as mixpanel from 'config/mixpanel';
import { SYLLABUS_MODE } from 'Domains/syllabus';
import LearningResultReportData from 'Domains/LearningResultReportData';

import { updatedFilter } from '../AttendanceFilter/utils';

import LearningResultBarCharts from './LearningResultBarCharts';
import LearningResultAttendanceTable from './LearningResultAttendanceTable';

const useStyles = makeStyles({
  container: {
    position: 'absolute',
    top: '50px',
    zIndex: 5,
    minWidth: '350px',
    width: '100%',
    maxWidth: '700px',
  },
});

const FilterBlock = styled(Box)`
  border-radius: 15px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
`;

const useQueryString = () => {
  return new URLSearchParams(useLocation().search);
};

const calculateGainScoreCategory = score => {
  if (score > 75) return 'ระดับสูงมาก';
  if (score > 50) return 'ระดับสูง';
  if (score > 25) return 'ระดับกลาง';
  if (score > 0) return 'ระดับต้น';
  return 'ไม่มี';
};

const convertMasteryTextToType = {
  LV1: 'เริ่มตั้งไข่',
  LV2: 'ตั้งไข่',
  LV3: 'เข้าใจ',
  LV4: 'เชี่ยวชาญ',
};

const LearningResult = ({
  isFetchData,
  setIsFetchData,
  setDisabledFetch,
  onFilterApplied,
  onLogAction,
  schoolCode,
  myProfileId,
  filterSlot,
}) => {
  const { send } = useAnalytics();
  const { t } = useTranslation('learningResult');
  const queryString = useQueryString();
  const currentPath = useLocation().pathname;
  const history = useHistory();
  const classes = useStyles();
  const [openFilter, setOpenFilter] = useState(false);
  const [currentFilter, setCurrentFilter] = useState({});
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [activeBar, setActiveBar] = useState(null);

  const theme = useContext(ThemeContext);

  const filterValue = {
    term: currentFilter?.term?.id ?? queryString.get('term') ?? null,
    grade:
      currentFilter?.grade?.id ??
      queryString.get('gradeFilter') ??
      queryString.get('grade') ??
      null,
    classNo:
      currentFilter?.classNo?.classNo ??
      queryString.get('classFilter') ??
      queryString.get('class') ??
      null,
    subject: currentFilter?.subject?.id ?? queryString.get('subj') ?? null,
    syllabus: currentFilter?.syllabus?.courseCode ?? queryString.get('course') ?? null,
    lesson: currentFilter?.lesson?.sectionCode ?? queryString.get('sect') ?? null,
    mode: currentFilter?.mode ?? queryString.get('mode') ?? SYLLABUS_MODE.TRADITIONAL,
    activeBar: activeBar,
  };

  useEffect(() => {
    if (!_.isEmpty(currentFilter)) {
      onFilterApplied({ ...currentFilter, activeBar });
    }
  }, [activeBar]);

  const handleChange = () => {
    setOpenFilter(prev => !prev);
  };

  const handleFilter = ({ action, filterData, queryString }) => {
    onLogAction({
      category: 'Filter',
      action: `Click`,
      label: `Click ${action} filter on learning result page`,
    });
    send(`${action}_filter`, { on: 'learning_result_page' });
    onFilterApplied(
      action === 'reset' ? { ...filterData } : { ...filterData, activeBar: activeBar }
    );
    setCurrentFilter(
      action === 'reset' ? { ...filterData } : { ...filterData, activeBar: activeBar }
    );
    setOpenFilter(false);
    if (action === 'submit') {
      mixpanel.trackEvent('Learning Result Filter Submit Click', {
        distinct_id: myProfileId,
        'Year Term': filterData?.term?.name,
        Grade: filterData?.grade?.name,
        Room: filterData?.classNo?.classNo,
        Subject: filterData?.subject?.name,
        Course: filterData?.syllabus?.courseName,
        Section: filterData?.lesson?.sectionName,
      });
    }
    history.push(`${currentPath}${queryString}`);
  };

  return (
    <>
      <Box>
        <AttendanceFilterData
          initialValue={filterValue}
          schoolCode={schoolCode}
          useLearningResultLessons
        >
          {({ list, value, updateValue }) => {
            const { term, grade, classNo, subject, syllabus, lesson, mode } = value;
            const { termList, gradeList, classList, subjectList, syllabusList, lessonList } = list;
            if (queryString.toString() !== '' && isFirstTime) {
              if (
                termList &&
                gradeList &&
                classList &&
                subjectList &&
                syllabusList &&
                ((syllabus && lessonList) || !syllabus)
              ) {
                if (term && grade && classNo && subject && syllabus && lesson) {
                  const filterData = getFilterData(list, value);
                  onFilterApplied({ ...filterData, activeBar });
                  setCurrentFilter({ ...filterData, activeBar });
                }
                setIsFirstTime(false);
              }
            } else {
              updatedFilter(list, value, updateValue, false);
            }

            const handleDisableFilterButton = action => {
              const values = Object.values(value);
              return action === 'submit' ? values.some(v => !v) : values.every(v => !v);
            };

            const filterBar = (
              <Flex alignItems="center">
                <ButtonIcon
                  flex="0 0 auto"
                  variant={'primary'}
                  textProp={{
                    fontWeight: 'semibold',
                  }}
                  lineHeight={'0.5'}
                  height={'40px'}
                  leftIcon={<Icon name={ICON_LIST.FILTER} />}
                  message={t('FILTER')}
                  onClick={handleChange}
                />
                <Collapse in={openFilter} className={classes.container}>
                  <FilterBlock width={1} p={7} maxWidth={'700px'} bg={'white'}>
                    <AttendanceFilter
                      isAddGradesOption={false}
                      isAddClassNosOption
                      isAddSyllabusOption
                      isAddLessonsOption
                      isSyllabusRequired={false}
                      list={list}
                      updateValue={updateValue}
                      value={value}
                      isInvalidFilter={handleDisableFilterButton('submit')}
                      onFilterApply={() =>
                        handleFilter({
                          action: 'submit',
                          filterData: { ...getFilterData(list, value), activeBar },
                          queryString: `?term=${term}&grade=${grade}&class=${classNo}&subj=${subject}&course=${syllabus}${
                            lesson !== null ? `&sect=${lesson}` : ''
                          }&mode=${mode}`,
                        })
                      }
                      onResetFilter={() => {
                        setActiveBar(null);
                        handleFilter({
                          action: 'reset',
                          filterData: {},
                          queryString: '',
                        });
                      }}
                      isDisableResetFilter={handleDisableFilterButton('reset')}
                      translation={t}
                    />
                  </FilterBlock>
                </Collapse>
                <Breadcrumb
                  maxItemsPerRow={3}
                  items={convertToBreadCrumb(currentFilter)}
                  p={0}
                  ml={4}
                />
              </Flex>
            );
            return filterSlot ? ReactDOM.createPortal(filterBar, filterSlot) : filterBar;
          }}
        </AttendanceFilterData>
      </Box>
      <Box style={{ overflow: 'hidden' }}>
        <Box style={{ overflow: 'auto', height: '100%' }}>
          {_.isEmpty(currentFilter) && (
            <Box>
              <Flex flexDirection={'column'} py={9}>
                <Flex mb={3} mx={'auto'}>
                  <Text>{t('SELECT_BUTTON')}</Text>
                </Flex>
                <Flex mb={3} mx={'auto'}>
                  <Image
                    maxHeight={'40px'}
                    src={'/assets/components/Icons/filter-button-icon.png'}
                    srcSet={
                      '/assets/components/Icons/filter-button-icon.png 1x, /assets/components/Icons/filter-button-icon@2x.png 2x, /assets/components/Icons/filter-button-icon@3x.png 3x'
                    }
                  />
                </Flex>
                <Flex mb={3} mx={'auto'}>
                  <Text>{t('TO_DISPLAYED')}</Text>
                </Flex>
              </Flex>
            </Box>
          )}
          {!_.isEmpty(currentFilter) && (
            <Box style={{ marginTop: '8px' }}>
              <LearningResultReportData value={filterValue} schoolCode={schoolCode}>
                {({ loading, error, data, refetch, networkStatus }) => {
                  if (loading || networkStatus === NetworkStatus.refetch) return <Loading />;
                  if (error) return null;
                  if (isFetchData) {
                    refetch();
                    setIsFetchData(false);
                  }
                  setDisabledFetch(false);

                  const { stats, students } = data?.foLearningResultReport;
                  const studentsFilter = students?.filter(student => {
                    const { growthRate, mastery, bloom } = student;

                    const filterGainScore =
                      activeBar?.label === calculateGainScoreCategory(growthRate);
                    const filterMastery = activeBar?.label === convertMasteryTextToType[mastery];
                    const filterBloom = bloom?.includes(activeBar?.label);
                    return filterGainScore || filterMastery || filterBloom;
                  });

                  const activeBadgeColor = {
                    GAIN_SCORE: theme.colors.primary,
                    MASTERY: theme.colors.masteryLV4,
                    BLOOM: theme.colors.bloomChart,
                  };

                  const activeBadgeText = {
                    GAIN_SCORE: `${t('GAIN_SCORE')}: ${
                      activeBar?.label === t('GAIN_SCORE_NONE')
                        ? t('NO_GROWTH_RATE')
                        : t('GROWTH') + activeBar?.label
                    }`,
                    MASTERY: `${t('MASTERY')}: ${activeBar?.label}`,
                    BLOOM: `${t('BLOOM')}: ${activeBar?.label}`,
                  };

                  return (
                    <>
                      <Text.SubTitle style={{ fontWeight: 600 }}>
                        {t('COURSE_GRADE_TITLE')}
                      </Text.SubTitle>
                      <LearningResultBarCharts
                        activeBar={activeBar}
                        setActiveBar={setActiveBar}
                        stats={stats}
                      />
                      {activeBar && (
                        <Box>
                          <Flex alignItems={'center'} mb={5}>
                            <Text.SubTitle style={{ fontWeight: 600, marginRight: '8px' }}>
                              {t('INDIVIDUAL_REPORT_TITLE')}
                            </Text.SubTitle>
                            <Box
                              style={{
                                padding: '4px 12px',
                                borderRadius: '6px',
                                backgroundColor: activeBadgeColor[activeBar?.type],
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color:
                                    activeBar?.type === 'GAIN_SCORE'
                                      ? 'white'
                                      : theme.colors.greyscale.grey1,
                                }}
                              >
                                {activeBadgeText[activeBar?.type]}
                              </Text>
                            </Box>
                          </Flex>
                          <LearningResultAttendanceTable data={studentsFilter} />
                        </Box>
                      )}
                    </>
                  );
                }}
              </LearningResultReportData>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

LearningResult.defaultProps = {
  onFilterApplied: {},
};

export default withAnalytic(LearningResult);
